package nl.volleyplanner.api.client.planner

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.datetime.LocalDate
import nl.volleyplanner.api.entities.FindPersonRequest
import nl.volleyplanner.api.entities.FullPerson
import nl.volleyplanner.api.entities.GroupListResponse
import nl.volleyplanner.api.GroupType
import nl.volleyplanner.api.entities.OrganisationSeason
import nl.volleyplanner.api.PersonActivity
import nl.volleyplanner.api.PersonInclude
import nl.volleyplanner.api.entities.PersonUpdateRequest

class PersonAndGroupsEndpoint(val httpClient: HttpClient, prefix: String) {
    private val personBase = "$prefix/person"

    suspend fun getPersons(
        include: Set<PersonInclude> = emptySet(),
        groups: List<GroupType> = emptyList(),
        season: OrganisationSeason
    ): List<FullPerson> {
        return httpClient.get("$personBase/list") {
            url.parameters.append("season", season.name)
            if (include.isNotEmpty()) {
                url.parameters.append("include", include.joinToString(",") { it.paramValue })
            }
            if (groups.isNotEmpty()) {
                url.parameters.append("groupTypes", groups.joinToString(",") { it.paramValue })
            }
        }.body()
    }

    suspend fun getPersonById(personId: String): List<FullPerson> {
        return httpClient.get("$personBase/$personId").body()
    }

    suspend fun getPersonByEmail(email: String): List<FullPerson> {
        return httpClient.post("$personBase/search") {
            contentType(ContentType.Application.Json)
            setBody(FindPersonRequest(email))
        }.body()
    }

    suspend fun updatePerson(personUpdateRequest: PersonUpdateRequest) = httpClient.put("$personBase/${personUpdateRequest.id}") {
        contentType(ContentType.Application.Json)
        setBody(personUpdateRequest)
    }

    suspend fun getPersonSchedule(personId: String, startDate: LocalDate? = null, endDate: LocalDate? = null, problemVersionSlug: String? = null): List<PersonActivity> {
        return httpClient.get("$personBase/$personId/schedule") {
            if (startDate != null) {
                url.parameters.append("start_date", startDate.toString())
            }
            if (endDate != null) {
                url.parameters.append("end_date", endDate.toString())
            }
            if (problemVersionSlug != null) {
                url.parameters.append("planning_problem_version", problemVersionSlug)
            }
        }.body()
    }
}

class GroupsEndpoint(val httpClient: HttpClient, prefix: String) {
    private val groupBase = "$prefix/group"

    suspend fun getGroupsByType(season: OrganisationSeason, groupType: GroupType) : GroupListResponse {
        return httpClient.get("$groupBase/${season.name}/list") {
            url.parameters.append("type", groupType.paramValue)
        }.body()
    }

    suspend fun setRefereeGroups(season: OrganisationSeason, groupNames: List<String>) {
        return httpClient.post("$groupBase/${season.name}/referee/update") {
            contentType(ContentType.Application.Json)
            setBody(groupNames)
        }.body()
    }
}